import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { alpha, Box, Chip, Paper, Stack, Typography } from '@mui/material'
import {
	DirectDealCategory,
	DirectDealStatus,
	DirectDealSummary,
} from '@services/directDeals.types'
import { numToCurrency } from '@utils/stringHelpers'

type Props = {
	deal: DirectDealSummary
}

export default function DirectDealCard({ deal }: Props) {
	const isAccepted = deal.status === DirectDealStatus.ACCEPTED
	const isRejected = deal.status === DirectDealStatus.REJECTED

	const isRecommended = deal.category === DirectDealCategory.RECOMMEND
	const isDecline = deal.category === DirectDealCategory.DECLINE

	let highlight = false
	if (isRecommended && isRejected) highlight = true
	if (isDecline && isAccepted) highlight = true
	const bgColor = highlight
		? (theme) => alpha(theme.palette.warning.light, 0.1)
		: 'background.paper'

	return (
		<Paper
			variant="outlined"
			sx={{
				p: '10px',
				borderRadius: 2,
				width: '100%',
				borderColor: highlight ? 'warning.main' : undefined,
				bgcolor: bgColor,
			}}
		>
			<Stack spacing={1}>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					data-testid={`deal-card-${deal.id}`}
				>
					<Typography
						variant="body1"
						fontWeight={600}
						noWrap
						data-testid={`deal-card-name-${deal.id}`}
					>
						{deal.manufacturerName}
					</Typography>
					<Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
						{(isAccepted || isRejected) && (
							<Chip
								size="small"
								variant="filled"
								color={isAccepted ? 'success' : 'error'}
								icon={
									<FontAwesomeIcon
										icon={isAccepted ? faCheck : faXmark}
										style={{ fontSize: 12, color: 'white' }}
									/>
								}
								label={isAccepted ? 'Accepted' : 'Rejected'}
								data-testid={`deal-card-status-${deal.id}`}
								sx={{ p: 0.5 }}
							/>
						)}
						<Chip
							size="small"
							variant="outlined"
							label={isRecommended ? 'Recommend' : 'Decline'}
							color={isRecommended ? 'success' : 'error'}
							data-testid={`deal-card-category-${deal.id}`}
							sx={{ p: 0.5 }}
						/>
					</Stack>
				</Stack>

				<Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
					<Stat
						label="Coverage"
						value={`${Math.round(deal.coverage)}%`}
						color={isRecommended ? 'success' : 'error'}
					/>
					<Stat
						label="Stocked"
						value={`${Math.round(deal.stocking)}%`}
						color={isRecommended ? 'success' : 'error'}
					/>
					<Stat
						label="Price Savings"
						value={numToCurrency(deal.annualPriceSavings, 'USD', 0)}
						color={isRecommended ? 'success' : 'error'}
					/>
					<Stat
						label="Rebate Savings"
						value={numToCurrency(deal.annualRebateSavings, 'USD', 0)}
						color={isRecommended ? 'success' : 'error'}
					/>
					<Stat
						label="Total Savings"
						value={numToCurrency(deal.annualTotalSavings, 'USD', 0)}
						color={isRecommended ? 'success' : 'error'}
					/>
				</Stack>
			</Stack>
		</Paper>
	)
}

type StatProps = {
	label: string
	value: string
	color: 'success' | 'error'
}

function Stat({ label, value, color }: StatProps) {
	return (
		<Box sx={{ minWidth: 100 }} data-testid={`deal-card-stat-${label.toLowerCase()}`}>
			<Typography variant="body2" fontWeight={600} color="text.secondary" noWrap>
				{label}
			</Typography>
			<Typography
				variant="h6"
				color={color}
				noWrap
				data-testid={`deal-card-stat-value-${label.toLowerCase()}`}
			>
				{value}
			</Typography>
		</Box>
	)
}
